/**
 * A question Claude asked the forge's owner (Claude Code's AskUserQuestion;
 * ADR-023 A12). The turn waits until the owner answers here: pick an option
 * per question — several where Claude allows it — or write your own under
 * "Other". The answers go back to Claude, which carries on in the same turn.
 */
import { useState } from 'react';
import { HelpCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { ForgePendingQuestion } from '../types';

const OTHER = '__other__';

interface Choice {
  selected: string[];
  other: string;
}

/** The answer text for one question, or '' while it is unanswered. */
export function answerFor(choice: Choice | undefined): string {
  if (!choice) return '';
  const labels = choice.selected.filter((s) => s !== OTHER);
  const other = choice.selected.includes(OTHER) ? choice.other.trim() : '';
  return [...labels, ...(other ? [other] : [])].join(', ');
}

export interface ForgeQuestionCardProps {
  question: ForgePendingQuestion;
  onAnswer: (answers: Record<string, string>) => Promise<void>;
}

export function ForgeQuestionCard({ question, onAnswer }: ForgeQuestionCardProps) {
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (text: string, value: string, multi: boolean) => {
    setChoices((prev) => {
      const current = prev[text] ?? { selected: [], other: '' };
      const on = current.selected.includes(value);
      const selected = multi
        ? on
          ? current.selected.filter((s) => s !== value)
          : [...current.selected, value]
        : [value];
      return { ...prev, [text]: { ...current, selected } };
    });
  };

  const setOther = (text: string, other: string) => {
    setChoices((prev) => {
      const current = prev[text] ?? { selected: [OTHER], other: '' };
      return { ...prev, [text]: { ...current, other } };
    });
  };

  const answers = Object.fromEntries(
    question.questions.map((q) => [q.question, answerFor(choices[q.question])]),
  );
  const complete = Object.values(answers).every((a) => a.length > 0);

  const submit = async () => {
    if (!complete || sending) return;
    setSending(true);
    setError(null);
    try {
      await onAnswer(answers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your answer');
      setSending(false);
    }
  };

  return (
    <section
      aria-label="Claude's question"
      data-testid="forge-question"
      className="max-h-[55vh] overflow-y-auto border-t bg-primary/5 px-4 py-3"
    >
      <p className="mb-2 flex items-center gap-2 text-sm font-medium">
        <HelpCircle className="h-4 w-4 text-primary" />
        Claude Code is waiting for your answer
      </p>
      <div className="space-y-3">
        {question.questions.map((q, qi) => {
          const choice = choices[q.question];
          const otherOn = choice?.selected.includes(OTHER) ?? false;
          const optionRole = q.multiSelect ? 'checkbox' : 'radio';
          return (
            <fieldset key={q.question} className="space-y-1.5">
              <legend className="text-sm">
                {q.header && (
                  <span className="mr-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {q.header}
                  </span>
                )}
                {q.question}
                {q.multiSelect && (
                  <span className="ml-1 text-xs text-muted-foreground">(choose any)</span>
                )}
              </legend>
              <div
                role={q.multiSelect ? 'group' : 'radiogroup'}
                aria-label={q.header || q.question}
                className="grid gap-1.5 sm:grid-cols-2"
              >
                {[...q.options, { label: 'Other', description: 'Write your own answer' }].map(
                  (option, oi) => {
                    const value = oi === q.options.length ? OTHER : option.label;
                    const checked = choice?.selected.includes(value) ?? false;
                    return (
                      <button
                        key={`${qi}-${value}`}
                        type="button"
                        role={optionRole}
                        aria-checked={checked}
                        disabled={sending}
                        onClick={() => toggle(q.question, value, q.multiSelect)}
                        className={cn(
                          'rounded-md border bg-background px-3 py-2 text-left text-sm transition-colors',
                          'hover:bg-accent disabled:opacity-60',
                          checked && 'border-primary ring-1 ring-primary',
                        )}
                      >
                        <span className="block font-medium">{option.label}</span>
                        {option.description && (
                          <span className="block text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </button>
                    );
                  },
                )}
              </div>
              {otherOn && (
                <Input
                  autoFocus
                  aria-label={`Your answer: ${q.question}`}
                  placeholder="Your answer"
                  value={choice?.other ?? ''}
                  maxLength={2000}
                  disabled={sending}
                  onChange={(e) => setOther(q.question, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submit();
                  }}
                />
              )}
            </fieldset>
          );
        })}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      )}
      <div className="mt-3 flex justify-end">
        <Button size="sm" onClick={() => void submit()} disabled={!complete || sending}>
          {sending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Send answer
        </Button>
      </div>
    </section>
  );
}
